<template>
  <v-card class="impermanent-loss-calculator">
    <v-card-title class="d-flex align-center">
      <v-icon color="primary" class="mr-2">mdi-calculator</v-icon>
      Calculadora de Impermanent Loss
      <v-spacer></v-spacer>
      <v-tooltip text="O que é Impermanent Loss?">
        <template v-slot:activator="{ props }">
          <v-btn
            icon="mdi-help-circle-outline"
            variant="text"
            size="small"
            v-bind="props"
            @click="showInfoDialog = true"
          ></v-btn>
        </template>
      </v-tooltip>
    </v-card-title>

    <v-card-text>
      <p class="text-body-2 mb-4">
        Esta calculadora ajuda a estimar o impacto potencial da variação de preços nos tokens de uma pool de liquidez.
      </p>

      <!-- Seleção de par de tokens -->
      <v-select
        v-model="selectedPool"
        :items="pools"
        item-title="name"
        label="Escolha um par de tokens"
        variant="outlined"
        class="mb-4"
        @update:model-value="resetResults"
      ></v-select>

      <!-- Preços iniciais (atuais) -->
      <div class="text-subtitle-1 mb-2">Preços Atuais</div>
      <v-row>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="initialPrice1"
            :label="`Preço de ${token1Label}`"
            variant="outlined"
            type="number"
            min="0.000001"
            step="0.000001"
            density="compact"
            readonly
            hide-details
            class="mb-4"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="initialPrice2"
            :label="`Preço de ${token2Label}`"
            variant="outlined"
            type="number"
            min="0.000001"
            step="0.000001"
            density="compact"
            readonly
            hide-details
            class="mb-4"
          ></v-text-field>
        </v-col>
      </v-row>

      <!-- Simulação de novos preços -->
      <div class="text-subtitle-1 mb-2">Simulação de Novos Preços</div>
      <v-row>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="newPrice1"
            :label="`Novo preço de ${token1Label}`"
            variant="outlined"
            type="number"
            min="0.000001"
            step="0.000001"
            density="compact"
            hide-details
            class="mb-4"
            @input="calculateImpermanentLoss"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="newPrice2"
            :label="`Novo preço de ${token2Label}`"
            variant="outlined"
            type="number"
            min="0.000001"
            step="0.000001"
            density="compact"
            hide-details
            class="mb-4"
            @input="calculateImpermanentLoss"
          ></v-text-field>
        </v-col>
      </v-row>

      <!-- Valor do investimento -->
      <div class="text-subtitle-1 mb-2">Investimento</div>
      <v-text-field
        v-model="investmentAmount"
        label="Valor do investimento em USD"
        variant="outlined"
        type="number"
        min="1"
        step="1"
        density="compact"
        hide-details
        class="mb-4"
        @input="calculateImpermanentLoss"
      ></v-text-field>

      <!-- Slider para variação de preço rápida -->
      <div class="text-subtitle-1 mb-2">Variação rápida de preço</div>
      <div class="d-flex align-center mb-4">
        <div class="text-body-2 mr-2">-90%</div>
        <v-slider
          v-model="priceChangePercent"
          min="-90"
          max="500"
          step="5"
          thumb-label
          hide-details
          @update:model-value="updatePriceFromSlider"
        ></v-slider>
        <div class="text-body-2 ml-2">+500%</div>
      </div>

      <!-- Resultados -->
      <v-divider class="my-4"></v-divider>
      
      <v-alert
        v-if="showResults"
        variant="tonal"
        :color="impermanentLossPercent > 5 ? 'error' : impermanentLossPercent > 1 ? 'warning' : 'success'"
        class="mb-4"
      >
        <div class="text-subtitle-1 font-weight-bold mb-2">Resultado da Simulação</div>
        
        <div class="d-flex justify-space-between mb-2">
          <div class="text-body-2">Impermanent Loss:</div>
          <div class="font-weight-medium">{{ formatPercent(impermanentLossPercent) }}</div>
        </div>
        
        <div class="d-flex justify-space-between mb-2">
          <div class="text-body-2">Valor Hold Puro:</div>
          <div class="font-weight-medium">$ {{ formatNumber(holdValue) }}</div>
        </div>
        
        <div class="d-flex justify-space-between mb-2">
          <div class="text-body-2">Valor após IL:</div>
          <div class="font-weight-medium">$ {{ formatNumber(lpValue) }}</div>
        </div>
        
        <div class="d-flex justify-space-between">
          <div class="text-body-2">Perda Estimada:</div>
          <div class="font-weight-medium">$ {{ formatNumber(impermanentLossValue) }}</div>
        </div>
      </v-alert>
      
      <v-alert
        v-if="showResults && estimatedApr > 0"
        variant="tonal"
        color="info"
        class="mb-4"
      >
        <div class="text-subtitle-1 font-weight-bold mb-2">Compensação com Taxas</div>
        <div class="text-body-2 mb-2">
          Com o APR atual de {{ formatPercent(estimatedApr) }}, você compensaria esta perda em aproximadamente:
        </div>
        <div class="text-h6 text-center">{{ formatTimeToRecover }}</div>
      </v-alert>

      <v-btn
        color="primary"
        size="large"
        block
        @click="calculateImpermanentLoss"
        :disabled="!isFormValid"
      >
        Calcular Impermanent Loss
      </v-btn>
    </v-card-text>

    <!-- Diálogo de informação sobre Impermanent Loss -->
    <v-dialog v-model="showInfoDialog" max-width="600">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon color="info" class="mr-2">mdi-information-outline</v-icon>
          O que é Impermanent Loss?
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" @click="showInfoDialog = false"></v-btn>
        </v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-3">
            <strong>Impermanent Loss</strong> (ou "Perda Temporária") é um fenômeno que ocorre quando você fornece liquidez a uma pool e o preço dos ativos muda em relação ao momento em que você os depositou.
          </p>
          
          <p class="text-body-1 mb-3">
            Quando você adiciona liquidez a uma pool na QuarnXSwap, você está depositando quantidades iguais (em valor) de dois tokens. Se o preço relativo desses tokens mudar, o protocolo de AMM (Automated Market Maker) automaticamente reequilibra sua posição.
          </p>
          
          <p class="text-body-1 mb-3">
            Esse reequilíbrio resulta em uma composição diferente do que se você simplesmente tivesse mantido os tokens originais - geralmente com menos valor do que o investimento original caso os preços tenham divergido significativamente.
          </p>
          
          <p class="text-body-1 mb-3">
            <strong>Por que "Impermanent"?</strong> Porque a perda só se torna permanente quando você remove sua liquidez. Se os preços voltarem para o valor original, a perda desaparece.
          </p>
          
          <p class="text-body-1 mb-3">
            <strong>Compensação:</strong> As taxas que você ganha fornecendo liquidez geralmente compensam essa perda ao longo do tempo, especialmente em pools com alto volume de transações.
          </p>
          
          <div class="text-h6 mb-2 mt-4">Fórmula:</div>
          <p class="text-body-2">
            A perda impermanente é calculada como a diferença entre manter tokens separadamente versus fornecer liquidez, dada por:
          </p>
          <div class="formula-box pa-3 my-3 text-center">
            IL = 2 × √(p₁/p₀) / (1 + p₁/p₀) - 1
          </div>
          <p class="text-caption text-medium-emphasis">
            Onde p₁/p₀ é a razão entre o preço final e o preço inicial dos tokens.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showInfoDialog = false">Entendi</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: 'ImpermanentLossCalculator',
  
  data() {
    return {
      selectedPool: null,
      initialPrice1: 2.5,
      initialPrice2: 2.5,
      newPrice1: 2.5,
      newPrice2: 2.5,
      investmentAmount: 1000,
      priceChangePercent: 0,
      changingToken: 1, // Qual token está sendo afetado pelo slider (1 ou 2)
      
      // Resultados do cálculo
      impermanentLossPercent: 0,
      holdValue: 0,
      lpValue: 0,
      impermanentLossValue: 0,
      
      showResults: false,
      showInfoDialog: false,
      
      // Dados das pools
      pools: (() => {
        const c = window.__QUARNX_COIN__ || 'tKinX'
        return [
          { id: 1, name: `${c}/QUSD`, token1: c, token2: 'QUSD', initialPrice1: 2.5, initialPrice2: 1, apr: 24.5 },
          { id: 2, name: `${c}/ETH`, token1: c, token2: 'ETH', initialPrice1: 2.5, initialPrice2: 3120, apr: 18.2 },
          { id: 3, name: `${c}/BNB`, token1: c, token2: 'BNB', initialPrice1: 2.5, initialPrice2: 530, apr: 16.8 },
          { id: 4, name: 'QUSD/ETH', token1: 'QUSD', token2: 'ETH', initialPrice1: 1, initialPrice2: 3120, apr: 14.5 }
        ]
      })()
    }
  },
  
  computed: {
    token1Label() {
      return this.selectedPool ? this.selectedPool.token1 : 'Token1'
    },
    
    token2Label() {
      return this.selectedPool ? this.selectedPool.token2 : 'Token2'
    },
    
    estimatedApr() {
      return this.selectedPool ? this.selectedPool.apr : 0
    },
    
    formatTimeToRecover() {
      if (!this.impermanentLossPercent || !this.estimatedApr) return 'N/A'
      
      // Calcular quantos dias seria necessário para recuperar a perda
      // baseado no APR (considerando juros simples)
      const daysToRecover = (this.impermanentLossPercent / (this.estimatedApr / 365)) || 0
      
      if (daysToRecover < 1) {
        return 'Menos de 1 dia'
      } else if (daysToRecover < 30) {
        return `${Math.ceil(daysToRecover)} dias`
      } else if (daysToRecover < 365) {
        return `${Math.ceil(daysToRecover / 30)} meses`
      } else {
        return `${(daysToRecover / 365).toFixed(1)} anos`
      }
    },
    
    isFormValid() {
      return this.selectedPool &&
        parseFloat(this.newPrice1) > 0 && 
        parseFloat(this.newPrice2) > 0 && 
        parseFloat(this.investmentAmount) > 0
    }
  },
  
  methods: {
    resetResults() {
      // Atualizar os preços iniciais com base na pool selecionada
      if (this.selectedPool) {
        this.initialPrice1 = this.selectedPool.initialPrice1
        this.initialPrice2 = this.selectedPool.initialPrice2
        this.newPrice1 = this.selectedPool.initialPrice1
        this.newPrice2 = this.selectedPool.initialPrice2
      }
      
      this.priceChangePercent = 0
      this.showResults = false
    },
    
    updatePriceFromSlider() {
      // Atualizar o preço do token selecionado com base no slider
      const factor = 1 + (this.priceChangePercent / 100)
      
      if (this.changingToken === 1) {
        this.newPrice1 = (this.initialPrice1 * factor).toFixed(6)
        this.newPrice2 = this.initialPrice2
      } else {
        this.newPrice1 = this.initialPrice1
        this.newPrice2 = (this.initialPrice2 * factor).toFixed(6)
      }
      
      this.calculateImpermanentLoss()
    },
    
    calculateImpermanentLoss() {
      if (!this.isFormValid) return
      
      // Preço inicial e novo dos tokens
      const p0A = parseFloat(this.initialPrice1)
      const p0B = parseFloat(this.initialPrice2)
      const p1A = parseFloat(this.newPrice1)
      const p1B = parseFloat(this.newPrice2)
      
      // Converter o investimento para quantidades iniciais de tokens
      const investmentUSD = parseFloat(this.investmentAmount)
      const halfInvestment = investmentUSD / 2
      
      const tokenAAmount = halfInvestment / p0A
      const tokenBAmount = halfInvestment / p0B
      
      // Valor de Hold: quanto valeria se simplesmente segurasse os tokens
      const holdValueA = tokenAAmount * p1A
      const holdValueB = tokenBAmount * p1B
      this.holdValue = holdValueA + holdValueB
      
      // Calcular o valor como provedor de liquidez (LP)
      const priceRatio = (p1A / p0A) / (p1B / p0B)
      const sqrtPriceRatio = Math.sqrt(priceRatio)
      
      this.impermanentLossPercent = (2 * sqrtPriceRatio / (1 + priceRatio) - 1) * 100
      this.impermanentLossValue = (this.impermanentLossPercent / 100) * this.holdValue
      this.lpValue = this.holdValue - Math.abs(this.impermanentLossValue)
      
      // Garantir que não haja valores negativos (absoluto)
      this.impermanentLossPercent = Math.abs(this.impermanentLossPercent)
      this.impermanentLossValue = Math.abs(this.impermanentLossValue)
      
      this.showResults = true
    },
    
    switchChangingToken() {
      this.changingToken = this.changingToken === 1 ? 2 : 1
    },
    
    formatNumber(value) {
      return parseFloat(value).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    
    formatPercent(value) {
      return parseFloat(value).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) + '%'
    }
  },
  
  mounted() {
    // Selecionar a primeira pool por padrão
    if (this.pools.length > 0) {
      this.selectedPool = this.pools[0]
      this.resetResults()
    }
  }
}
</script>

<style scoped>
.impermanent-loss-calculator {
  max-width: 100%;
  margin: 0 auto;
}

.formula-box {
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-weight: 500;
}
</style>
